import {
  type ContactResourceType,
  type ContactResponse,
  type OutreachContactId,
} from "shared/composables/useContact";
import { type ContactFilter } from "shared/composables/useContactsSearchFiltering";
import { type SortBy } from "shared/helpers/outreach";
import { type Nullable, type SortingOrder } from "shared/types";
import type {
  Influencer,
  Outlet,
  PrivateInfluencer,
} from "shared/types/outreach";

import ApiClient from "./ApiClient";

export interface OutreachContactRequestParams {
  contact_types?: ContactResourceType[];
  filters?: ContactFilter;
  include_contact_list_ids?: boolean;
  include_counts?: boolean;
  include_nested?: boolean;
  order?: Nullable<{
    sort_by: SortBy;
    sort_order: SortingOrder;
  }>;
  page_number?: number;
  page_size?: number;
}

interface AllOutletRequestParams {
  page_size: number;
  parent_outlet_id: number;
  parent_relationship: string;
}

interface AllPrivateInfluencerRequestParams {
  name: string;
  page_size: number;
  sort_by: "id";
  sort_order: SortingOrder;
}

interface OutreachContactStatsResponse {
  ids: OutreachContactId[];
  ids_with_emails: Record<OutreachContactId, string>;
}

class OutreachContactService extends ApiClient {
  constructor() {
    super("/");
  }

  getAllContacts(
    params: OutreachContactRequestParams,
    options?: { signal?: AbortSignal }
  ) {
    return this.post<ContactResponse>("contacts", {
      params,
      ...options,
    });
  }

  getAllOutlets(params: AllOutletRequestParams) {
    return this.get<Outlet[]>("outlets/search", {
      params,
    });
  }

  getInfluencer(id: number, options?: { signal?: AbortSignal }) {
    return this.get<Influencer>(`influencers/${id}/influencer`, options);
  }

  getOutlet(id: number, options?: { signal?: AbortSignal }) {
    return this.get<Outlet>(`outlets/${id}/outlet`, options);
  }

  getStats(
    params: OutreachContactRequestParams & {
      stat_type?: "ids" | "ids_with_emails";
    },
    options?: { signal?: AbortSignal }
  ) {
    return this.post<OutreachContactStatsResponse>("contacts/stats", {
      params,
      ...options,
    });
  }

  searchPrivateInfluencers(params: AllPrivateInfluencerRequestParams) {
    return this.get<PrivateInfluencer[]>("private_influencers/search", {
      params,
    });
  }
}

export default new OutreachContactService();
